import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiMenu, IoMdClose } from './icons';
import { Link, useLocation } from 'react-router-dom';
import Modal from './Modal';
import { useUser } from './UserContext';
import Login from './Login';
import Register from './Register';
import styles from '../css/modules/HamburgerMenu.module.css';
import UserProfile from './UserProfile';
import { LICENCJA_PAGES } from '../seo/siteMeta';

const PANEL_ID = 'main-menu-panel';

/* Elementy, na które można ustawić fokus wewnątrz panelu - potrzebne
   do pułapki na Tab (menu jest oknem modalnym, więc fokus nie może z niego uciec). */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const MENU_LINKS = [
  { to: '/intro', label: 'Darmowy test' },
  ...LICENCJA_PAGES.map(p => ({
    to: p.path,
    label: p.rodzina === 'bsp' ? `Dron ${p.krotko}` : p.nazwa,
  })),
  { to: '/nauka/ppl-a', label: 'Nauka według przedmiotów' },
  { to: '/egzamin-teoretyczny-ulc', label: 'Egzamin w ULC' },
  { to: '/cennik', label: 'Cennik' },
  { href: '/poradnik', label: 'Poradnik dla pilotów' },
];

const HamburgerMenu = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const { user, logout } = useUser();
  const location = useLocation();

  const toggleRef = useRef(null);
  const panelRef = useRef(null);

  const close = useCallback(() => setIsOpen(false), []);

  /* Zmiana adresu (np. klik w link) zawsze zamyka menu - inaczej po przejściu
     na nową stronę panel zostawał otwarty i zasłaniał treść. */
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  /* Blokada przewijania tła bez przesuwania układu.
     `overflow: hidden` na <body> chowa pionowy pasek przewijania, przez co
     na desktopie treść skoczyłaby w bok o jego szerokość - rekompensujemy to
     paddingiem. Na telefonach pasek jest nakładkowy, więc rekompensata = 0. */
  useEffect(() => {
    if (!isOpen) return undefined;

    const { body, documentElement } = document;
    const gutter = window.innerWidth - documentElement.clientWidth;
    const previousOverflow = body.style.overflow;
    const previousPadding = body.style.paddingRight;

    body.style.overflow = 'hidden';
    // pasek przewijania nigdy nie jest szerszy niż ~20 px; większa różnica
    // oznaczałaby poziome przepełnienie strony, którego nie wolno kompensować
    if (gutter > 0 && gutter <= 20) {
      const current = parseFloat(window.getComputedStyle(body).paddingRight) || 0;
      body.style.paddingRight = `${current + gutter}px`;
    }

    return () => {
      body.style.overflow = previousOverflow;
      body.style.paddingRight = previousPadding;
    };
  }, [isOpen]);

  /* Escape zamyka, Tab krąży wewnątrz panelu. */
  useEffect(() => {
    if (!isOpen) return undefined;

    const onKeyDown = event => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        close();
        return;
      }
      if (event.key !== 'Tab' || !panelRef.current) return;

      const items = [...panelRef.current.querySelectorAll(FOCUSABLE)].filter(
        el => el.offsetParent !== null || el === document.activeElement
      );
      if (items.length === 0) return;

      const first = items[0];
      const last = items[items.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      } else if (!panelRef.current.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, close]);

  /* Fokus wchodzi do panelu po otwarciu i wraca na przycisk po zamknięciu. */
  const wasOpen = useRef(false);
  useEffect(() => {
    /* preventScroll jest konieczne: fokus w elemencie position: fixed
       kazał przeglądarce przewinąć stronę na samą górę. */
    if (isOpen) {
      wasOpen.current = true;
      const target = panelRef.current?.querySelector(FOCUSABLE);
      target?.focus({ preventScroll: true });
    } else if (wasOpen.current) {
      wasOpen.current = false;
      toggleRef.current?.focus({ preventScroll: true });
    }
  }, [isOpen]);

  const openModal = setter => {
    close();
    setter(true);
  };

  const panel = (
    <div className={styles.overlay} onClick={close}>
      <div
        id={PANEL_ID}
        ref={panelRef}
        className={styles.panel}
        role="dialog"
        aria-modal="true"
        aria-label="Menu główne"
        onClick={event => event.stopPropagation()}
      >
        <button type="button" className={styles.closeButton} onClick={close} aria-label="Zamknij menu">
          <IoMdClose size={28} aria-hidden="true" focusable="false" />
        </button>

        <nav className={styles.menuContent} aria-label="Menu mobilne">
          {MENU_LINKS.map(item =>
            item.to ? (
              <Link key={item.to} to={item.to} className={styles.menuText} onClick={close}>
                {item.label}
              </Link>
            ) : (
              <a key={item.href} href={item.href} className={styles.menuText} onClick={close}>
                {item.label}
              </a>
            )
          )}

          {user ? (
            <>
              <button
                type="button"
                onClick={() => openModal(setIsRegisterModalOpen)}
                className={styles.button}
              >
                Profil użytkownika
              </button>
              <button
                type="button"
                onClick={() => {
                  close();
                  logout();
                }}
                className={styles.button}
              >
                Wyloguj
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => openModal(setIsLoginModalOpen)}
              className={styles.button}
            >
              Logowanie
            </button>
          )}
        </nav>
      </div>
    </div>
  );

  return (
    <div className={styles.hamburgerMenu}>
      <button
        type="button"
        ref={toggleRef}
        onClick={() => setIsOpen(open => !open)}
        className={styles.menuIcon}
        aria-label={isOpen ? 'Zamknij menu' : 'Otwórz menu'}
        aria-expanded={isOpen}
        aria-controls={PANEL_ID}
        aria-haspopup="dialog"
      >
        {isOpen ? (
          <IoMdClose size={28} aria-hidden="true" focusable="false" />
        ) : (
          <FiMenu size={28} aria-hidden="true" focusable="false" />
        )}
      </button>

      {/* Panel żyje w <body>: dzięki temu nie może poszerzyć nagłówka
          ani dokumentu (to była przyczyna przesuwania strony w bok). */}
      {isOpen && typeof document !== 'undefined' && createPortal(panel, document.body)}

      {/* Modale renderujemy poza panelem, żeby nie były przez niego przycinane. */}
      {user ? (
        <Modal
          isOpen={isRegisterModalOpen}
          onClose={() => setIsRegisterModalOpen(false)}
          ariaLabel="Profil użytkownika"
        >
          <UserProfile closeModal={() => setIsRegisterModalOpen(false)} />
        </Modal>
      ) : (
        <>
          <Modal
            isOpen={isLoginModalOpen}
            onClose={() => setIsLoginModalOpen(false)}
            ariaLabel="Logowanie"
          >
            <Login closeModal={() => setIsLoginModalOpen(false)} />
          </Modal>
          <Modal
            isOpen={isRegisterModalOpen}
            onClose={() => setIsRegisterModalOpen(false)}
            ariaLabel="Rejestracja"
          >
            <Register />
          </Modal>
        </>
      )}
    </div>
  );
};

export default HamburgerMenu;
